import { format } from 'date-fns';
import { nanoid } from 'nanoid';
import cmsParser from 'Common/util/cmsParser';
import htmlParser from 'html-react-parser';
import { Twitter } from '@solent-university/solent-icons';

import type { PageEntry, Node } from 'contensis-delivery-api';
import type { Props } from './Coaching';

export type PageData = {
    entry: PageEntry;
    ancestors: Node[];
};

const datamap = (data: PageData): Props => {
    const { entry, ancestors } = data;
    const {
        name,
        courseOverview,
        coachingCoursesOverview,
        relatedCoursesLink,
        twitterLink,
        coachingBookingInformation
    } = entry;

    const nanoidLength = 10;
    const maxCards = 4;

    return {
        breadcrumbs: cmsParser.breadcrumbs(ancestors),
        heroBanner: {
            heading: name
        },
        courseOverview: courseOverview.map(item => {
            switch (item.type) {
                case 'text':
                    return {
                        text: htmlParser(item.value)
                    };
                case 'videoTranscript':
                    return { video: cmsParser.transcriptEmbed(item.value) };
                case 'youTubeVideo':
                    return { video: cmsParser.video(item.value) };
                default:
                    return {};
            }
        }),
        supportBlock: {
            children: htmlParser(
                `<p>${entry.prerequisites}</p><h3>Course duration:</h3><p>${entry.duration}</p>`
            ),
            heading: 'Course pre-requisites:'
        },
        information: {
            image: cmsParser.image(coachingCoursesOverview.image),
            heading: 'Coach education courses at solent',
            children: htmlParser(coachingCoursesOverview.text)
        },
        whatYoullStudy: {
            heading: entry.whatYoullStudyHeading,
            text: htmlParser(entry.whatYoullStudyText)
        },
        keyPoint: {
            heading: entry.examsCertificatesHeading,
            children: entry.examsAndCertificates
                ? htmlParser(entry.examsAndCertificates)
                : undefined
        },
        lineList:
            entry.fees.length > 0
                ? {
                      items: entry.fees[0].item.map(fee => ({
                          text: fee,
                          id: `linelist_${nanoid(nanoidLength)}`
                      }))
                  }
                : undefined,
        feeText: entry.feesUpdateText,
        questionsBlock: {
            children: entry.coachingBookingText ? htmlParser(entry.coachingBookingText) : undefined,
            heading: 'Got a Question?'
        },
        social: {
            icon: {
                as: Twitter,
                colour: '#1DA1F2'
            },
            link: {
                href: twitterLink.url,
                label: 'Follow us on Twitter for updates'
            }
        },
        bookings: {
            items: coachingBookingInformation.map(booking => ({
                courseRef: booking.courseInstanceReference,
                date: format(new Date(booking.datetime?.from), 'E dd MMM yyyy'),
                time: `${format(new Date(booking.datetime?.from), 'hh:mm')} ${
                    booking.datetime.to && '-'
                } ${format(new Date(booking.datetime?.to), 'hh:mm')}`,
                venue: booking.location.name,
                link: cmsParser.link({
                    ...booking,
                    label: 'Book online',
                    ariaLabel: `Book online for ${booking.courseInstanceReference} of this coaching course`
                }),
                id: `booking_${nanoid(nanoidLength)}`
            }))
        },
        bookingQueryText: entry.coachingBookingText
            ? htmlParser(entry.coachingBookingText)
            : undefined,
        card: relatedCoursesLink.slice(0, maxCards).map(rcl => ({
            heading: rcl.name,
            image: cmsParser.image(rcl.courseCardImage),
            children: <p>{rcl.courseDescription}</p>,
            link: {
                label: 'Find out more',
                ariaLabel: `Find out more about ${rcl.name}`,
                href: rcl.sys.uri
            },
            id: rcl.sys.id
        })),
        bannerLink: {
            link: cmsParser.link(entry.sportsComplexLink)
        }
    };
};

export default datamap;
